import React from "react";

import { observer } from "mobx-react-lite";

import type { ContextMenuItem } from "@synthesis/shared-ui";

import type { FileMetaDto } from "../../api";
import { ExplorerView, useStores } from "../../store";
import { type ContextMenuStateFunctions } from "../files/hooks";
import { ModalReName } from "../explorer/local-components";
import { TableView } from "../files/local-components";
import { GridView, ListView, ModalDeleteFolder } from "./local-components";

type FolderProps = {
  id?: string;
};

type FolderViewProps = {
  onClick: (data: FileMetaDto) => void;
  onContextMenu: (data: FileMetaDto) => void;
  contextMenuState: ContextMenuItem[];
};

const toDateSeconds = (value: string | number): number => {
  if (typeof value === "number") return value;
  return Math.floor(Date.parse(value) / 1000) || 0;
};

const noopFileHandler = (_file?: FileMetaDto) => undefined;
const noopHandler = () => undefined;

const folderTableStateFunctions: ContextMenuStateFunctions = {
  openViewer: noopFileHandler,
  openDoc: noopFileHandler,
  handleClickReName: noopHandler,
  handleDeleteFile: noopHandler,
  setIsMarkupOpen: noopHandler,
  handleClickAddNewVersion: noopHandler,
  setIsOpenMeta: noopHandler,
  handleClickConvert: noopHandler,
};

const FolderView = observer(({ onClick, onContextMenu, contextMenuState }: FolderViewProps) => {
  const { generalStore, directoryStore } = useStores();

  if (directoryStore.directories.length === 0) {
    return null;
  }

  if (generalStore.view === ExplorerView.Table) {
    return (
      <TableView onClickModel={noopFileHandler} stateFunctions={folderTableStateFunctions} setSelectedFile={noopFileHandler} disabledRight={false} />
    );
  }

  if (generalStore.view === ExplorerView.List) {
    return (
      <>
        {directoryStore.directories.map((data) => (
          <ListView.Folder
            key={data.id}
            data={{
              id: data.id,
              baseName: data.baseName,
              path: data.baseName,
              createdAt: toDateSeconds(data.createdAt),
              modifiedAt: toDateSeconds(data.modifiedAt),
            }}
            onClick={() => onClick(data)}
            onContextMenu={(_e, folder) => onContextMenu({ ...data, baseName: folder.baseName })}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {directoryStore.directories.map((data) => (
        <GridView.Folder
          key={data.id}
          data={{
            id: data.id,
            baseName: data.baseName,
            modifiedAt: toDateSeconds(data.modifiedAt),
          }}
          onClick={() => onClick(data)}
          onContextMenu={() => onContextMenu(data)}
          contextMenuState={contextMenuState}
        />
      ))}
    </>
  );
});

const Folders = observer<FolderProps>(({ id: _id }) => {
  const { generalStore, fileStore } = useStores();
  const [isOpenDeleteFolder, setIsOpenDeleteFolder] = React.useState(false);
  const [selectedFolder, setSelectedFolder] = React.useState<FileMetaDto | null>(null);
  const [isOpenReName, setIsOpenReName] = React.useState(false);

  const handleClick = (data: FileMetaDto) => {
    generalStore.historyGo({ id: data.id, parentId: data.parentId ?? null, name: data.baseName });
  };

  const onContextMenu = (data: FileMetaDto) => {
    setSelectedFolder(data);
  };

  const handleClickDelete = () => {
    setIsOpenDeleteFolder(true);
  };

  const closeModalReName = () => {
    setIsOpenReName(false);
    setSelectedFolder(null);
  };

  const handleOpenModalReName = () => {
    setIsOpenReName(true);
  };

  const closeModalDeleteFolder = () => {
    setIsOpenDeleteFolder(false);
    setSelectedFolder(null);
  };

  const openModalDeleteFolder = () => {
    setIsOpenDeleteFolder(true);
  };

  const contextMenuState: ContextMenuItem[] = [
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            label: "Удалить папку",
            onClick: handleClickDelete,
            dataCy: "folders-menuItem-deleteItem",
            activeRed: true,
          } as ContextMenuItem,
        ]
      : []),
    {
      label: generalStore.checkedFiles.size > 1 ? "Скачать выбранные архивом" : "Скачать архив",
      onClick: () => {
        if (!selectedFolder) return;
        fileStore.downloadArchiveFile({
          id: selectedFolder.id,
          baseName: selectedFolder.baseName || "Безымянный",
          ext: selectedFolder.ext ?? "",
        });
      },
    },
    ...(generalStore.checkedFiles.size <= 1
      ? [
          {
            label: "Переименовать папку",
            onClick: handleOpenModalReName,
          },
        ]
      : []),
  ];

  return (
    <>
      {isOpenReName && selectedFolder && (
        <ModalReName
          name={selectedFolder.baseName}
          id={selectedFolder.id}
          type={selectedFolder.type}
          isOpen={isOpenReName}
          setIsOpen={setIsOpenReName}
          openModal={handleOpenModalReName}
          closeModal={closeModalReName}
        />
      )}
      <FolderView onClick={handleClick} onContextMenu={onContextMenu} contextMenuState={contextMenuState} />
      {isOpenDeleteFolder && (
        <ModalDeleteFolder
          deleteId={selectedFolder?.id}
          isOpen={isOpenDeleteFolder}
          setIsOpen={setIsOpenDeleteFolder}
          openModal={openModalDeleteFolder}
          closeModal={closeModalDeleteFolder}
        />
      )}
    </>
  );
});

export default Folders;
